import { useEffect, useMemo, useState } from 'react'
import {
  Send, ShieldCheck, AlertTriangle, ArrowDownLeft, ArrowUpRight, CheckCircle2, Search,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Field, NumberInput, Empty, Rows, CopyButton } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { useAuth, toast } from '../store'
import { get, post, ApiError } from '../lib/api'
import { clsx, num, fa, toEnDigits } from '../lib/format'

type Asset = {
  currency: string; faName: string; color: string; balance: number
  priceIrt: number; valueIrt: number
}

type Config = {
  enabled: boolean; feeRate: number; minIrt: number
  dailyCapIrt: number; dailyUsedIrt: number
  kycRequired: number; kycOk: boolean; irtAllowed: boolean; otpRequired: boolean
}

type Quote = {
  currency: string; amount: number; fee: number; total: number; valueIrt: number
  balance: number; receiver: string; dailyUsedIrt: number; dailyCapIrt: number
}

type Item = {
  ref: string; direction: 'in' | 'out'; currency: string; amount: number
  fee: number; counterparty: string; note: string; createdAt: string
}

export default function TransferPage() {
  const { user } = useAuth()
  const [cfg, setCfg] = useState<Config | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [items, setItems] = useState<Item[] | null>(null)

  const [identifier, setIdentifier] = useState('')
  const [receiver, setReceiver] = useState('')
  const [currency, setCurrency] = useState('USDT')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [quote, setQuote] = useState<Quote | null>(null)
  const [smsCode, setSmsCode] = useState('')
  const [smsSent, setSmsSent] = useState<{ mobile: string; expiresIn: number } | null>(null)
  const [otp, setOtp] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<Item | null>(null)

  const load = () => {
    get('/wallet/transfer/config').then(setCfg).catch(() => {})
    get('/wallet/balances').then((r: any) => setAssets(r.assets ?? [])).catch(() => {})
    get('/wallet/transfer/history').then((r: any) => setItems(r.items ?? []))
      .catch(() => setItems([]))
  }
  useEffect(load, [])

  // تومان فقط وقتی در فهرست می‌آید که مدیر اجازه داده باشد
  const options = useMemo(
    () => assets.filter((a) => (cfg?.irtAllowed ? true : a.currency !== 'IRT')),
    [assets, cfg],
  )
  const selected = options.find((a) => a.currency === currency) ?? null

  // با هر تغییر مبلغ/ارز/گیرنده، تأییدِ قبلی باطل می‌شود تا کاربر چیزی را
  // تأیید نکند که دیگر معتبر نیست
  useEffect(() => { setQuote(null); setSmsSent(null); setSmsCode('') },
    [identifier, currency, amount])

  const lookup = async () => {
    if (!identifier.trim()) return
    try {
      const r: any = await post('/wallet/transfer/lookup', { identifier: identifier.trim() })
      setReceiver(r.receiver)
    } catch (e: any) {
      setReceiver('')
      toast.err(e.message)
    }
  }

  const preview = async () => {
    setBusy(true)
    try {
      const r: any = await post('/wallet/transfer/quote', {
        identifier: identifier.trim(), currency, amount: Number(toEnDigits(amount)),
      })
      setQuote(r)
      setReceiver(r.receiver)
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  const submit = async () => {
    setBusy(true)
    try {
      const r: any = await post('/wallet/transfer', {
        identifier: identifier.trim(), currency, amount: Number(toEnDigits(amount)),
        note, sms_code: smsCode, otp,
      })
      setDone(r)
      setIdentifier(''); setReceiver(''); setAmount(''); setNote('')
      setQuote(null); setSmsCode(''); setSmsSent(null); setOtp('')
      toast.ok('انتقال با موفقیت انجام شد')
      load()
    } catch (e: any) {
      // بک‌اند برای مرحله کد پیامکی، ۴۰۱ با جزئیات ساختاریافته برمی‌گرداند
      const d = e instanceof ApiError ? e.detail : null
      if (d && typeof d === 'object' && d.code === 'sms_required') {
        setSmsSent({ mobile: d.mobile ?? '', expiresIn: d.expiresIn ?? 0 })
        toast.ok(d.message || 'کد تأیید برای شما پیامک شد')
      } else {
        toast.err(e.message)
      }
    } finally { setBusy(false) }
  }

  const remaining = cfg && cfg.dailyCapIrt > 0
    ? Math.max(0, cfg.dailyCapIrt - cfg.dailyUsedIrt) : null

  if (cfg && !cfg.enabled) {
    return (
      <PanelShell title="انتقال داخلی" subtitle="ارسال رمزارز به کاربران ارزینو">
        <Card>
          <Empty text="انتقال داخلی در حال حاضر غیرفعال است." icon={<Send className="h-8 w-8" />} />
        </Card>
      </PanelShell>
    )
  }

  return (
    <PanelShell title="انتقال داخلی" subtitle="ارسال آنی و بدون کارمزد به کاربران ارزینو">
      <div className="grid gap-5 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-up/25 bg-up/[.06] p-3 text-[11px] leading-6 text-up">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              انتقال بین کاربران ارزینو <b>آنی</b> و <b>بدون کارمزد</b> انجام می‌شود، چون
              روی بلاک‌چین ثبت نمی‌شود و کارمزد شبکه ندارد.
            </div>
          </div>

          {cfg && !cfg.kycOk && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-gold/30 bg-gold/[.07] p-3 text-[11px] leading-6 text-gold">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              برای استفاده از انتقال داخلی باید احراز هویت سطح {fa(cfg.kycRequired)} را کامل کنید.
            </div>
          )}

          <Field label="گیرنده" hint="شماره موبایل، ایمیل یا کد معرف کاربر مقصد">
            <div className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="09121234567 یا user@mail.com"
                value={identifier}
                onChange={(e) => { setIdentifier(e.target.value); setReceiver('') }}
                onBlur={lookup}
              />
              <Button variant="ghost" onClick={lookup} className="shrink-0">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </Field>

          {receiver && (
            <div className="-mt-2 mb-4 flex items-center gap-2 rounded-lg border border-up/25 bg-up/[.06] px-3 py-2 text-xs text-up">
              <CheckCircle2 className="h-3.5 w-3.5" />
              گیرنده: <b>{receiver}</b>
            </div>
          )}

          <Field label="ارز">
            <select className="input" value={currency}
              onChange={(e) => setCurrency(e.target.value)}>
              {options.map((a) => (
                <option key={a.currency} value={a.currency} className="bg-ink-900">
                  {a.faName} ({a.currency}) — موجودی {num(a.balance, 8)}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="مبلغ"
            hint={selected
              ? `موجودی قابل انتقال: ${num(selected.balance, 8)} ${selected.currency}`
              : undefined}
          >
            <NumberInput value={amount} onChange={setAmount} placeholder="0.00"
              suffix={<span className="text-xs text-slate-400">{currency}</span>} />
          </Field>

          <Field label="یادداشت (اختیاری)" hint="فقط برای خودتان در تاریخچه ثبت می‌شود">
            <input className="input" value={note} maxLength={200}
              onChange={(e) => setNote(e.target.value)} placeholder="بابت…" />
          </Field>

          {remaining !== null && (
            <div className="mb-4 text-[11px] text-slate-500">
              سقف انتقال روزانه: {num(cfg!.dailyCapIrt, 0)} تومان ·
              باقی‌مانده امروز: <b className="text-slate-300">{num(remaining, 0)} تومان</b>
            </div>
          )}

          {!quote ? (
            <Button className="w-full" loading={busy} onClick={preview}
              disabled={!identifier.trim() || !amount || !!(cfg && !cfg.kycOk)}>
              بررسی و ادامه
            </Button>
          ) : (
            <div className="rounded-xl border border-white/10 bg-white/[.03] p-4">
              <div className="mb-3 text-xs font-semibold text-slate-300">تأیید نهایی</div>
              <Row label="گیرنده" value={quote.receiver} />
              <Row label="مبلغ" value={`${num(quote.amount, 8)} ${quote.currency}`} />
              <Row label="کارمزد" value={quote.fee > 0
                ? `${num(quote.fee, 8)} ${quote.currency}` : 'رایگان'} />
              <Row label="کسر از حساب شما"
                value={`${num(quote.total, 8)} ${quote.currency}`} strong />

              <div className="my-3 flex items-start gap-2 rounded-lg border border-down/25 bg-down/[.06] p-2.5 text-[10px] leading-5 text-down">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                این انتقال <b>آنی و برگشت‌ناپذیر</b> است. پس از تأیید، فقط با رضایت
                گیرنده قابل بازگشت خواهد بود. نام گیرنده را دوباره بررسی کنید.
              </div>

              {user?.twoFa && (
                <Field label="کد ورود دومرحله‌ای">
                  <input className="input num text-center" dir="ltr" maxLength={6}
                    value={otp} onChange={(e) => setOtp(toEnDigits(e.target.value))} />
                </Field>
              )}

              {smsSent && (
                <Field label="کد پیامک‌شده"
                  hint={smsSent.mobile ? `ارسال‌شده به ${smsSent.mobile}` : undefined}>
                  <input className="input num text-center" dir="ltr" maxLength={8}
                    value={smsCode} onChange={(e) => setSmsCode(toEnDigits(e.target.value))} />
                </Field>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => setQuote(null)}>
                  انصراف
                </Button>
                <Button className="flex-1" loading={busy} onClick={submit}>
                  {smsSent ? 'تأیید و انتقال' : 'انتقال'}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <div className="mb-3 text-sm font-semibold text-slate-200">تاریخچه انتقال‌ها</div>
          {done && (
            <div className="mb-3 rounded-xl border border-up/30 bg-up/[.07] p-3 text-xs text-up">
              انتقال انجام شد — کد پیگیری{' '}
              <b className="num">{done.ref}</b>
              <CopyButton text={done.ref} />
            </div>
          )}
          {items === null ? <Rows n={5} /> : items.length === 0 ? (
            <Empty text="هنوز انتقالی انجام نداده‌اید" icon={<Send className="h-7 w-7" />} />
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.ref}
                  className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[.02] p-3">
                  <div className={clsx('grid h-8 w-8 shrink-0 place-items-center rounded-lg',
                    it.direction === 'in' ? 'bg-up/15 text-up' : 'bg-down/15 text-down')}>
                    {it.direction === 'in'
                      ? <ArrowDownLeft className="h-4 w-4" />
                      : <ArrowUpRight className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-xs text-slate-200">
                      <CoinIcon symbol={it.currency} size={14} />
                      <b className="num">{num(it.amount, 8)}</b> {it.currency}
                    </div>
                    <div className="truncate text-[10px] text-slate-500">
                      {it.direction === 'in' ? 'از' : 'به'} {it.counterparty}
                      {it.note ? ` · ${it.note}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-left">
                    <div className="num text-[10px] text-slate-500">{it.ref}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </PanelShell>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className={clsx('num', strong ? 'font-semibold text-slate-100' : 'text-slate-300')}>
        {value}
      </span>
    </div>
  )
}
